/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
import { defineNuxtPlugin, useHead, useRuntimeConfig } from 'nuxt/app'
import { installDeferredObserver } from '~/utility/deferred-observer'

const _layer = 'dataLayer'

function gtmClient() {
  return {
    push(obj) {
      if (!window[_layer]) {
        window[_layer] = []
      }
      window[_layer].push(obj)
    },
  }
}

function gtmServer() {
  const events: any[] = []
  return {
    push(obj: any) {
      events.push(obj)
    },
    unshift(obj: any) {
      events.unshift(obj)
    },
    render(_gtmId: string) {
      let script =
        'window.gmo = window.gmo || {}; window.dataLayer = window.dataLayer || [];'
      while (events.length) {
        const event = events.shift()
        script += `${_layer}.push(${JSON.stringify(event)});`
      }
      // Only render the dataLayer initialization script during SSR.
      // The actual GTM container is loaded client-side after hydration
      // to improve web vitals (LCP, INP, TBT).
      useHead({
        script: [
          {
            id: 'data-layer',
            innerHTML: script,
          },
        ],
      })
    },
  }
}

/**
 * Load the GTM container script. Pushes the gtm.start event to the
 * dataLayer and creates the async script element.
 * Guarded against double-loading.
 */
function loadGtmScript(gtmId: string) {
  if ((window as any).__gtmLoaded) return
  ;(window as any).__gtmLoaded = true

  // Push GTM start event (same as the original inline snippet)
  window[_layer] = window[_layer] || []
  window[_layer].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `/_metrics/gtm.js?id=${gtmId}`
  document.head.appendChild(script)
}

/**
 * Load GTM only after CookieYes has determined consent state.
 *
 * Why: The 29 Dec 2025 consent change (default=deny for EU/EEA) introduced a
 * race condition. On page reload after consent, GTM could load before CookieYes
 * restored the stored consent from cookies — causing purchase hits to fire under
 * "denied". GA4 is session-based and resilient to this, but SA360's GA-imported
 * revenue requires each ecommerce hit to have clean consent state. Hits under
 * "denied" (with no cookieless pings) don't qualify for GA→SA360 import.
 *
 * Fix: Wait for CookieYes `cookieyes_banner_load` event (fires once consent
 * state is known — either new user or returning user's stored consent restored).
 * Then load GTM so every tag fires under the correct consent state from hit #1.
 *
 * Fallback: If CookieYes script fails to load (ad blocker, network error,
 * or silently blocked), load GTM anyway. A 5s safety-net timeout catches
 * cases where the script is blocked without triggering an error event.
 */
function loadGtmAfterConsent(gtmId: string) {
  let loaded = false

  const load = (reason: string, detail?: unknown) => {
    if (loaded) return
    loaded = true
    try {
      console.info(`[GTM] ${reason}`, detail ?? '')
    } catch {
      // Logging should never prevent GTM from loading
    }
    loadGtmScript(gtmId)
  }

  const waitForConsentAction = () => {
    document.addEventListener(
      'cookieyes_consent_update',
      (updateEvent: Event) => {
        load(
          'User consent action completed',
          (updateEvent as CustomEvent).detail,
        )
      },
      { once: true },
    )
  }

  const handleBannerLoad = (event: Event) => {
    try {
      const detail = (event as CustomEvent).detail
      if (detail?.isUserActionCompleted) {
        load('Returning user — consent already stored', detail)
      } else {
        console.info('[GTM] New user — waiting for consent action', detail)
        waitForConsentAction()
      }
    } catch {
      // If event detail is malformed, load GTM to avoid silent failure
      load('CookieYes banner_load event error — loading GTM')
    }
  }

  // 1. Listen for CookieYes init. For returning users (consent already stored),
  //    load GTM immediately. For new users (banner shown), wait for their action.
  document.addEventListener('cookieyes_banner_load', handleBannerLoad, {
    once: true,
  })

  // 2. Guard against missed event: if CookieYes already fired banner_load
  //    before our listener was attached (async script can beat hydration),
  //    detect via the consent cookie or visible banner.
  const hasConsentCookie = document.cookie.includes('cookieyes-consent')
  if (hasConsentCookie) {
    load('Returning user — consent cookie already present')
  } else if (document.querySelector('.cky-consent-container')) {
    console.info(
      '[GTM] CookieYes banner already visible — waiting for user action',
    )
    waitForConsentAction()
  }

  // 3. Fallback: if CookieYes script fails to load (ad blocker, network error,
  //    or silently blocked without triggering error), load GTM anyway.
  const cookieyesScript = document.getElementById('cookieyes')
  if (cookieyesScript) {
    cookieyesScript.addEventListener('error', () => {
      load('CookieYes script failed to load — loading GTM')
    })
  } else {
    load('CookieYes script not found — loading GTM')
  }

  // 4. Safety net: if neither CookieYes events nor error fired after 5s
  //    (e.g. ad blocker silently blocks without error), check if the consent
  //    banner is visible. If it is, the user hasn't acted yet — keep waiting.
  //    If not, CookieYes is truly blocked — load GTM.
  setTimeout(() => {
    if (loaded) return
    if (document.querySelector('.cky-consent-container')) {
      console.info('[GTM] CookieYes banner visible — waiting for user action')
    } else {
      load('CookieYes did not respond within 5s — loading GTM')
    }
  }, 5000)
}

function startPageTracking(gtm) {
  const router = useRouter()
  const runtimeConfig = useRuntimeConfig()
  router.afterEach((to) => {
    setTimeout(() => {
      gtm.push(
        to.meta.gtm || {
          routeName: to.name,
          pageType: 'PageView',
          pageUrl: runtimeConfig.public.envUrl + to.fullPath,
          pageTitle: (typeof document !== 'undefined' && document.title) || '',
          event: 'nuxtRoute',
        },
      )
    }, 250)
  })
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const gtm = process.client ? gtmClient() : gtmServer()

  // Make GTM available to Vue instance
  Object.defineProperty(nuxtApp.vueApp.config.globalProperties, '$gtm', {
    value: gtm,
    writable: false,
    configurable: true,
    enumerable: true,
  })

  gtm.push({
    isSSR: process.server,
  })

  if (process.server) {
    nuxtApp.hooks.hook('app:rendered', () => {
      const gtmServer = gtm as ReturnType<typeof gtmServer>
      gtmServer.unshift({
        alfredVersion: config.public.alfredVersion,
        config: config.public,
        event: 'appInit',
      })
      if (config.public.gtm && config.public.gtm.id && gtmServer.render) {
        gtmServer.render(config.public.gtm.id)
      }
    })
  }

  // Client-side: install performance hooks and load GTM.
  // Wait for CookieYes consent state before loading GTM to ensure every tag
  // fires under the correct consent state from the first hit. Returning users
  // load immediately; new users wait for consent action. Falls back to loading
  // GTM directly if CookieYes is blocked or fails to load.
  if (process.client) {
    const gtmId = config.public.gtm?.id as string | undefined
    if (gtmId) {
      installDeferredObserver()
      loadGtmAfterConsent(gtmId)
    }
  }

  startPageTracking(gtm)
  return {
    provide: {
      gtm,
    },
  }
})
